import { useState, useCallback, useRef } from 'react';
import type { GeneralSettings, BrandingSettings, ContactoSettings, RedesSettings, AppsSettings, SocialNetwork } from '@/mocks/adminSettingsRadio';
import { categoriasRadio, paisesLatam, tipografias } from '@/mocks/adminSettingsRadio';
import { SwalAlert } from '@/hooks/useSweetAlert';
import AdminImageUpload from '@/components/base/AdminImageUpload';

interface Props {
  general: GeneralSettings;
  branding: BrandingSettings;
  contacto: ContactoSettings;
  redes: RedesSettings;
  apps: AppsSettings;
  onChangeGeneral: (data: GeneralSettings) => void;
  onChangeBranding: (data: BrandingSettings) => void;
  onChangeContacto: (data: ContactoSettings) => void;
  onChangeRedes: (data: RedesSettings) => void;
  onChangeApps: (data: AppsSettings) => void;
  onSave: () => void;
  saving: boolean;
}

export default function GeneralTab({
  general, branding, contacto, redes, apps,
  onChangeGeneral, onChangeBranding, onChangeContacto, onChangeRedes, onChangeApps,
  onSave, saving,
}: Props) {
  const [logoPreview, setLogoPreview] = useState(branding.logo);
  const [coverPreview, setCoverPreview] = useState(branding.cover);
  const logoBlobRef = useRef<string | null>(null);
  const coverBlobRef = useRef<string | null>(null);

  const handleLogoChange = useCallback((file: File) => {
    if (logoBlobRef.current) URL.revokeObjectURL(logoBlobRef.current);
    const blobUrl = URL.createObjectURL(file);
    logoBlobRef.current = blobUrl;
    setLogoPreview(blobUrl);
    onChangeBranding({ ...branding, logo: blobUrl });
    SwalAlert.toast('Logo cargado correctamente.', 'success');
  }, [branding, onChangeBranding]);

  const handleLogoRemove = useCallback(() => {
    if (logoBlobRef.current) URL.revokeObjectURL(logoBlobRef.current);
    logoBlobRef.current = null;
    onChangeBranding({ ...branding, logo: '' });
    setLogoPreview('');
  }, [branding, onChangeBranding]);

  const handleCoverChange = useCallback((file: File) => {
    if (coverBlobRef.current) URL.revokeObjectURL(coverBlobRef.current);
    const blobUrl = URL.createObjectURL(file);
    coverBlobRef.current = blobUrl;
    setCoverPreview(blobUrl);
    onChangeBranding({ ...branding, cover: blobUrl });
    SwalAlert.toast('Portada cargada correctamente.', 'success');
  }, [branding, onChangeBranding]);

  const handleCoverRemove = useCallback(() => {
    if (coverBlobRef.current) URL.revokeObjectURL(coverBlobRef.current);
    coverBlobRef.current = null;
    onChangeBranding({ ...branding, cover: '' });
    setCoverPreview('');
  }, [branding, onChangeBranding]);

  const updateContactoToggle = (field: keyof ContactoSettings) => {
    if (typeof contacto[field] === 'boolean') {
      onChangeContacto({ ...contacto, [field]: !contacto[field] });
    }
  };

  const updateRed = (id: string, field: keyof SocialNetwork, value: string | boolean) => {
    onChangeRedes({
      redes: redes.redes.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    });
  };

  const validate = useCallback((): boolean => {
    if (!general.nombreRadio.trim()) {
      SwalAlert.warning('Nombre requerido', 'El nombre de la radio no puede estar vacío.');
      return false;
    }
    if (!general.categoria) {
      SwalAlert.warning('Categoría requerida', 'Debés seleccionar una categoría para tu radio.');
      return false;
    }
    return true;
  }, [general]);

  const handleSave = () => {
    if (validate()) {
      onSave();
    }
  };

  return (
    <div className="space-y-6">
      {/* ─── INFO BÁSICA ─── */}
      <div>
        <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
          <i className="ri-information-line text-foreground-500 text-xs"></i>
          Información Básica
        </h4>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-foreground-600 text-[10px] font-semibold mb-1">
                Nombre de la radio <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={general.nombreRadio}
                onChange={(e) => onChangeGeneral({ ...general, nombreRadio: e.target.value })}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none transition-colors focus:border-foreground-300"
                placeholder="Ej: Radio Nueva Actitud"
              />
            </div>
            <div>
              <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Eslogan</label>
              <input
                type="text"
                value={general.eslogan}
                onChange={(e) => onChangeGeneral({ ...general, eslogan: e.target.value })}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none transition-colors focus:border-foreground-300"
                placeholder="La radio que te mueve"
                maxLength={80}
              />
              <p className="text-foreground-300 text-[9px] text-right mt-0.5">{general.eslogan.length}/80</p>
            </div>
          </div>

          <div>
            <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Descripción</label>
            <textarea
              value={general.descripcion}
              onChange={(e) => onChangeGeneral({ ...general, descripcion: e.target.value })}
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors resize-none"
            ></textarea>
            <p className="text-foreground-300 text-[9px] text-right mt-0.5">{general.descripcion.length}/500</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Ciudad</label>
              <input
                type="text"
                value={general.ciudad}
                onChange={(e) => onChangeGeneral({ ...general, ciudad: e.target.value })}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
              />
            </div>
            <div>
              <label className="block text-foreground-600 text-[10px] font-semibold mb-1">País</label>
              <select
                value={general.pais}
                onChange={(e) => onChangeGeneral({ ...general, pais: e.target.value })}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors cursor-pointer appearance-none"
              >
                {paisesLatam.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-foreground-600 text-[10px] font-semibold mb-1">
                Categoría <span className="text-red-500">*</span>
              </label>
              <select
                value={general.categoria}
                onChange={(e) => onChangeGeneral({ ...general, categoria: e.target.value })}
                className={`w-full px-3 py-2 bg-background-50 border rounded-xl text-xs outline-none transition-colors cursor-pointer appearance-none ${!general.categoria ? 'border-red-300' : 'border-background-200 focus:border-foreground-300'}`}
              >
                <option value="">Seleccionar categoría...</option>
                {categoriasRadio.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Idioma</label>
              <select
                value={general.idioma}
                onChange={(e) => onChangeGeneral({ ...general, idioma: e.target.value })}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors cursor-pointer appearance-none"
              >
                {['Español', 'English', 'Português', 'Français'].map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Zona Horaria</label>
              <select
                value={general.zonaHoraria}
                onChange={(e) => onChangeGeneral({ ...general, zonaHoraria: e.target.value })}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors cursor-pointer appearance-none"
              >
                {[
                  'America/Guatemala (GMT-6)', 'America/Mexico_City (GMT-6)', 'America/Bogota (GMT-5)',
                  'America/Lima (GMT-5)', 'America/Santiago (GMT-4)', 'America/Argentina/Buenos_Aires (GMT-3)',
                  'America/Sao_Paulo (GMT-3)', 'Europe/Madrid (GMT+1)', 'America/New_York (GMT-5)',
                ].map((z) => (
                  <option key={z} value={z}>{z}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <hr className="border-background-200" />

      {/* ─── BRANDING ─── */}
      <div>
        <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
          <i className="ri-palette-line text-foreground-500 text-xs"></i>
          Branding
        </h4>
        <div className="space-y-4">
          <div>
            <AdminImageUpload
              label="Logo de la Radio"
              currentImage={logoPreview}
              onChange={handleLogoChange}
              onRemove={handleLogoRemove}
              maxSizeMB={2}
              recommendedDimensions="512×512px"
              previewHeight="h-20"
              previewWidth="w-20"
              placeholderIcon="ri-image-line"
            />
          </div>

          <div>
            <AdminImageUpload
              label="Imagen de Portada"
              currentImage={coverPreview}
              onChange={handleCoverChange}
              onRemove={handleCoverRemove}
              recommendedDimensions="1600×400px"
              previewHeight="h-32"
              placeholderIcon="ri-image-line"
            />
          </div>

          <div>
            <label className="block text-foreground-600 text-[10px] font-semibold mb-2">Paleta de Colores</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { field: 'colorPrimary' as keyof BrandingSettings, label: 'Principal', icon: 'ri-contrast-drop-fill' },
                { field: 'colorSecondary' as keyof BrandingSettings, label: 'Secundario', icon: 'ri-contrast-drop-line' },
                { field: 'colorAccent' as keyof BrandingSettings, label: 'Acento', icon: 'ri-paint-brush-line' },
              ].map((c) => (
                <div key={c.field}>
                  <div className="flex items-center gap-1.5 text-foreground-400 text-[9px] mb-1.5">
                    <i className={`${c.icon} text-[10px]`}></i>{c.label}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg border border-background-200 shrink-0" style={{ backgroundColor: branding[c.field] }}></div>
                    <input
                      type="text"
                      value={branding[c.field]}
                      onChange={(e) => onChangeBranding({ ...branding, [c.field]: e.target.value })}
                      className="flex-1 px-2.5 py-1.5 bg-background-50 border border-background-200 rounded-lg text-xs font-mono outline-none focus:border-foreground-300 transition-colors"
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-3 p-3 rounded-xl bg-background-50 border border-background-200">
              <span className="text-foreground-400 text-[9px] shrink-0">Vista previa:</span>
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-5 rounded-full" style={{ backgroundColor: branding.colorPrimary }}></div>
                <div className="w-5 h-5 rounded-full" style={{ backgroundColor: branding.colorSecondary }}></div>
                <div className="w-5 h-5 rounded-full" style={{ backgroundColor: branding.colorAccent }}></div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Tipografía</label>
            <select
              value={branding.tipografia}
              onChange={(e) => onChangeBranding({ ...branding, tipografia: e.target.value })}
              className="w-full sm:w-48 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors cursor-pointer appearance-none"
            >
              {tipografias.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <hr className="border-background-200" />

      {/* ─── CONTACTO ─── */}
      <div>
        <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
          <i className="ri-mail-line text-foreground-500 text-xs"></i>
          Contacto
        </h4>
        <div className="space-y-4">
          <div>
            <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Email de contacto</label>
            <input
              type="email"
              value={contacto.email}
              onChange={(e) => onChangeContacto({ ...contacto, email: e.target.value })}
              placeholder="contacto@miradio.com"
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Teléfono</label>
              <input
                type="text"
                value={contacto.telefono}
                onChange={(e) => onChangeContacto({ ...contacto, telefono: e.target.value })}
                placeholder="+502 5555 1234"
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
              />
            </div>
            <div>
              <label className="block text-foreground-600 text-[10px] font-semibold mb-1">WhatsApp</label>
              <input
                type="text"
                value={contacto.whatsapp}
                onChange={(e) => onChangeContacto({ ...contacto, whatsapp: e.target.value })}
                placeholder="+502 5555 1234"
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
              />
            </div>
          </div>
          <div>
            <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Dirección</label>
            <input
              type="text"
              value={contacto.direccion}
              onChange={(e) => onChangeContacto({ ...contacto, direccion: e.target.value })}
              placeholder="Av. Central 456, Zona 1"
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Ciudad</label>
              <input
                type="text"
                value={contacto.ciudadContacto}
                onChange={(e) => onChangeContacto({ ...contacto, ciudadContacto: e.target.value })}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
              />
            </div>
            <div>
              <label className="block text-foreground-600 text-[10px] font-semibold mb-1">País</label>
              <select
                value={contacto.paisContacto}
                onChange={(e) => onChangeContacto({ ...contacto, paisContacto: e.target.value })}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors cursor-pointer appearance-none"
              >
                {paisesLatam.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="p-3 rounded-xl bg-background-50 border border-background-200 space-y-2">
            <p className="text-foreground-700 text-[10px] font-semibold">Visibilidad en perfil público</p>
            {[
              { field: 'mostrarEmail' as keyof ContactoSettings, label: 'Mostrar Email', icon: 'ri-mail-line' },
              { field: 'mostrarTelefono' as keyof ContactoSettings, label: 'Mostrar Teléfono', icon: 'ri-phone-line' },
              { field: 'mostrarWhatsapp' as keyof ContactoSettings, label: 'Mostrar WhatsApp', icon: 'ri-whatsapp-line' },
            ].map((item) => (
              <div key={item.field} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className={`${item.icon} text-foreground-400 text-xs`}></i>
                  <span className="text-foreground-700 text-xs">{item.label}</span>
                </div>
                <button
                  onClick={() => updateContactoToggle(item.field)}
                  className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${contacto[item.field] ? 'bg-emerald-500' : 'bg-background-300'}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-background-50 rounded-full shadow-sm transition-transform ${contacto[item.field] ? 'translate-x-[16px]' : 'translate-x-0.5'}`}></span>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <hr className="border-background-200" />

      {/* ─── REDES SOCIALES ─── */}
      <div>
        <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
          <i className="ri-share-line text-foreground-500 text-xs"></i>
          Redes Sociales
        </h4>
        <div className="space-y-1.5">
          {redes.redes.map((red) => (
            <div key={red.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${red.activo ? 'bg-background-50 border-background-200' : 'bg-background-100/50 border-background-100'}`}>
              <button
                onClick={() => updateRed(red.id, 'activo', !red.activo)}
                className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${red.activo ? 'bg-emerald-500' : 'bg-background-300'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-background-50 rounded-full shadow-sm transition-transform ${red.activo ? 'translate-x-[16px]' : 'translate-x-0.5'}`}></span>
              </button>
              <div className="flex items-center gap-2.5 w-28 shrink-0">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${red.activo ? 'bg-background-200' : 'bg-background-200/50'}`}>
                  <i className={`${red.icon} ${red.activo ? 'text-foreground-600' : 'text-foreground-300'} text-sm`}></i>
                </div>
                <span className={`text-xs font-medium ${red.activo ? 'text-foreground-800' : 'text-foreground-300'}`}>{red.platform}</span>
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  value={red.url}
                  onChange={(e) => updateRed(red.id, 'url', e.target.value)}
                  disabled={!red.activo}
                  placeholder={`https://${red.id}.com/turadio`}
                  className={`w-full px-3 py-2 rounded-xl text-xs outline-none transition-colors ${red.activo ? 'bg-background-50 border border-background-200 focus:border-foreground-300' : 'bg-background-100 border border-background-100 text-foreground-300 cursor-not-allowed'}`}
                />
              </div>
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${red.activo && red.url ? 'bg-emerald-500' : 'bg-background-300'}`}></span>
            </div>
          ))}
        </div>
        <p className="text-foreground-400 text-[10px] mt-1.5">
          {redes.redes.filter((r) => r.activo && r.url).length} de {redes.redes.length} redes activas con URL
        </p>
      </div>

      <hr className="border-background-200" />

      {/* ─── APPS ─── */}
      <div>
        <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
          <i className="ri-smartphone-line text-foreground-500 text-xs"></i>
          Apps
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-4 rounded-2xl bg-background-50 border border-background-200 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center shrink-0">
                <i className="ri-apple-fill text-foreground-600 text-xl"></i>
              </div>
              <div>
                <p className="text-foreground-800 text-xs font-bold">App Store (iOS)</p>
                <p className="text-foreground-400 text-[10px]">v{apps.iosVersion || '—'}</p>
              </div>
            </div>
            <input
              type="text"
              value={apps.iosAppUrl}
              onChange={(e) => onChangeApps({ ...apps, iosAppUrl: e.target.value })}
              placeholder="https://apps.apple.com/app/..."
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs font-mono outline-none focus:border-foreground-300 transition-colors"
            />
          </div>
          <div className="p-4 rounded-2xl bg-background-50 border border-background-200 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center shrink-0">
                <i className="ri-google-play-fill text-foreground-600 text-xl"></i>
              </div>
              <div>
                <p className="text-foreground-800 text-xs font-bold">Google Play (Android)</p>
                <p className="text-foreground-400 text-[10px]">v{apps.androidVersion || '—'}</p>
              </div>
            </div>
            <input
              type="text"
              value={apps.androidAppUrl}
              onChange={(e) => onChangeApps({ ...apps, androidAppUrl: e.target.value })}
              placeholder="https://play.google.com/store/..."
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs font-mono outline-none focus:border-foreground-300 transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2 border-t border-background-200">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </button>
      </div>
    </div>
  );
}